import React, { useMemo, useCallback, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Comment } from '@distribute/shared/types';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { JSONContent } from '@tiptap/react';
import { Avatar, Button, Dropdown, Icon, Tooltip } from '../../../shared/ui';
import { formatRelativeTime } from '../utils/formatRelativeTime';
import { authUserModel } from '../../../entities/auth-user';
import { EditorHtmlContent } from '../../../entities/tiptap-editor';
import { IconMap } from '../../../shared/sprite';
import { commentsModel } from '../model';
import { useTiptapEditor } from '../../../entities/tiptap-editor';
import { CommentsEditorForm } from './CommentsEditorForm';

type CommentCardProps = {
  threadId: string;
  comment: Comment;
  isResolved: boolean;
  isMain?: boolean;
};

export const CommentCard: React.FC<CommentCardProps> = ({
  comment,
  threadId,
  isResolved = false,
  isMain = false,
}) => {
  const dispatch = useDispatch();
  const [isEditMode, setEditMode] = useState(false);
  const currentUser = useSelector(authUserModel.selectors.selectUserWithError);
  const isCommentEditing = useSelector(
    commentsModel.selectors.selectIsCommentEditing
  );
  const isOwner = currentUser.id === comment.createdById;
  const userName = `${comment.createdBy?.displayName} ${
    isOwner ? '(You)' : ''
  }`;
  const { editor } = useTiptapEditor();

  const handleResolve = () => {
    dispatch(
      commentsModel.actions.updateThreadStatus({ threadId, resolved: true })
    );
  };

  const handleUnResolve = useCallback(() => {
    dispatch(
      commentsModel.actions.updateThreadStatus({ threadId, resolved: false })
    );
  }, [dispatch, threadId]);

  const toggleEdit = useCallback(() => {
    setEditMode((prev) => !prev);
  }, []);

  const handleDeleteComment = useCallback(() => {
    if (isMain) {
      editor?.commands.removeThread(threadId);
      dispatch(commentsModel.actions.deleteThread({ threadId }));
    } else {
      dispatch(
        commentsModel.actions.deleteComment({
          threadId: threadId,
          commentId: comment.id,
        })
      );
    }
  }, [dispatch, threadId, editor, isMain, comment.id]);

  const handleEditUpdate = (content: JSONContent) => {
    dispatch(
      commentsModel.actions.updateCommentContent({
        threadId,
        commentId: comment.id,
        content,
        cb: (err?: unknown) => {
          if (!err) setEditMode(false);
        },
      })
    );
  };

  const actionItems = useMemo(() => {
    return [
      {
        id: 'edit',
        label: 'Edit',
        iconName: IconMap.Edit02,
        iconWidth: 16,
        isShow: isOwner && !isResolved,
        onClick: toggleEdit,
      },
      {
        id: 'unresolved',
        label: 'Mark as unresolved',
        iconName: IconMap.NotificationBox,
        iconWidth: 16,
        isShow: isResolved && isMain,
        onClick: handleUnResolve,
      },
      {
        id: 'delete',
        label: isMain ? 'Delete thread' : 'Delete',
        iconName: IconMap.Delete,
        iconWidth: 16,
        isShow: true,
        onClick: handleDeleteComment,
      },
    ].filter(({ isShow }) => isShow);
  }, [
    handleDeleteComment,
    toggleEdit,
    isOwner,
    isResolved,
    handleUnResolve,
    isMain,
  ]);

  return (
    <div className="flex flex-col gap-3 p-4">
      <div className="flex justify-between">
        <div className="flex items-center gap-3">
          <Avatar
            src={comment.createdBy?.photoUrl}
            displayName={comment.createdBy?.displayName || ''}
          />
          <div>
            <p className="font-semibold text-gray-700 text-sm">{userName}</p>
            <span className="text-gray-500 text-xs">
              {formatRelativeTime(comment.createdAt)}
            </span>
          </div>
        </div>
        <div className="flex items-center text-gray-600">
          {/*<Button*/}
          {/*  variant="icon"*/}
          {/*  color="transparent"*/}
          {/*  iconLeftName={IconMap.ArrowCircleUpRight}*/}
          {/*  iconBlockClassName="-rotate-90"*/}
          {/*  iconLeftWidth={20}*/}
          {/*/>*/}
          {!isResolved && isMain && (
            <Tooltip
              hideArrow
              trigger={
                <Button
                  size="sm"
                  variant="icon"
                  color="transparent"
                  iconLeftName={IconMap.CheckCircle}
                  iconBlockClassName=""
                  iconLeftWidth={20}
                  onClick={handleResolve}
                />
              }
            >
              Mark as resolved
            </Tooltip>
          )}
          <Dropdown
            triggerComponent={
              <DropdownMenu.Trigger className="rounded-lg flex items-center justify-center focus:outline-none text-gray-600 hover:bg-gray-50 h-9 w-9">
                <span className="flex items-center justify-center">
                  <Icon glyph={IconMap.DotsVertical} className="w-5 h-5" />
                </span>
              </DropdownMenu.Trigger>
            }
            listStyles="shadow-lg"
            isModalMode={false}
            items={actionItems}
          />
        </div>
      </div>
      <div className="[&>*:first-child]:pb-0 [&_p]:!text-md [&_p]:!mb-0">
        {isEditMode ? (
          <CommentsEditorForm
            isEditable
            initialValue={comment.contentJson}
            onCancel={toggleEdit}
            onSubmit={handleEditUpdate}
            disabled={isCommentEditing}
          />
        ) : (
          <EditorHtmlContent htmlContent={comment.htmlContent} />
        )}
      </div>
    </div>
  );
};
